import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardHeader from "../components/dashboard/DashboardHeader";
import ServerStatusSection, { ServerStatusData } from "../components/dashboard/ServerStatusSection";
import BrainTab from "../components/dashboard/BrainTab";
import ServicesTab from "../components/dashboard/ServicesTab";
import AccountingPanel from "../components/dashboard/AccountingPanel";
import ServerLinks from "../components/ServerLinks";
import InstallationDocSection from "../components/dashboard/InstallationDocSection";
import StarryBackground from "../components/StarryBackground";
import SpaceScene from "../components/SpaceScene";
import { Suspense } from "react";
import { toast } from "../hooks/use-toast";
import { handleExport } from "../utils/exportUtils";
import { fetchBrainDashboard } from "@/services/brainDashboardService";
import { fetchServerStatus } from "@/services/serverStatusService";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const defaultStatus: ServerStatusData = {
  cpu: "N/A",
  memory: "N/A",
  disk: "N/A",
  uptime: "N/A",
  nginx_status: "nepoznat",
  matrix_status: "nepoznat",
  api_status: "nepoznat",
  active_connections: "0",
};

const Dashboard = () => {
  const navigate = useNavigate();
  
  const [serverStatus, setServerStatus] = useState<ServerStatusData>(defaultStatus);
  const [brain, setBrain] = useState<any>(null);

  const handleExportData = (format: string) => {
    toast({
      title: "Izvoz podataka",
      description: `Podaci su izvezeni u ${format} formatu.`,
    });
    handleExport(format, serverStatus);
  };

  const handleLogout = () => {
    localStorage.removeItem("7gc_auth");
    toast({ title: "Odjava", description: "Uspješno ste se odjavili." });
    navigate("/");
  };

  const handleDownload = () => {
    toast({ title: "Preuzimanje dokumentacije", description: "Dokumentacija se preuzima..." });
    handleExport("INSTALL", serverStatus);
  };

  const refreshAll = async () => {
    try {
      const ss = await fetchServerStatus();
      setServerStatus(ss);
      localStorage.setItem("7gc_server_status", JSON.stringify(ss));
      toast({ title: "Status servera", description: "Podaci su uspješno osvježeni." });
    } catch (e) {
      console.log("[server-status] refresh fail:", e);
      toast({
        title: "Greška",
        description: "Nije moguće dohvatiti podatke o serveru.",
        variant: "destructive",
      });
    }

    try {
      const bd = await fetchBrainDashboard();
      setBrain(bd);
      if (bd?.ok && bd?.latest?.dispatch) {
        setServerStatus((prev) => ({ ...prev, api_status: "aktivan" }));
      }
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    document.title = "7GC Dashboard";
    document.body.style.overflow = "auto";
    
    const stored = localStorage.getItem("7gc_server_status");
    if (stored) {
      try {
        setServerStatus(JSON.parse(stored));
      } catch {
        /* ignore */
      }
    }

    refreshAll();
    const iv = setInterval(refreshAll, 30000);
    return () => {
      clearInterval(iv);
      document.body.style.overflow = "hidden";
    };
  }, []);

  return (
    <div className="min-h-screen text-foreground relative overflow-y-auto">
      <StarryBackground count={300} />
      <Suspense fallback={
        <div className="w-full h-full flex items-center justify-center">
          <div className="text-primary text-xl animate-pulse">Učitavanje planete...</div>
        </div>
      }>
        <SpaceScene />
      </Suspense>
      
      <div className="relative z-10 pb-20">
        <DashboardHeader onExport={handleExportData} onLogout={handleLogout} />
        <main className="container mx-auto p-6 space-y-8">
          <Tabs defaultValue="status" className="w-full">
            <TabsList className="bg-black/50 border border-primary/20 mb-4">
              <TabsTrigger value="status">Status Servera</TabsTrigger>
              <TabsTrigger value="brain">Brain</TabsTrigger>
              <TabsTrigger value="services">Services</TabsTrigger>
              <TabsTrigger value="prevodi">Prevodi</TabsTrigger>
            </TabsList>
            <TabsContent value="status">
              <ServerStatusSection serverStatus={serverStatus} />
            </TabsContent>
            <TabsContent value="brain">
              <BrainTab brain={brain} />
            </TabsContent>
            <TabsContent value="services">
              <ServicesTab />
            </TabsContent>
            <TabsContent value="prevodi">
              <AccountingPanel />
            </TabsContent>
          </Tabs>
          
          <section className="mt-10">
            <h2 className="text-xl mb-6 flex items-center gap-2 text-foreground/80">
              🔗 Server linkovi
            </h2>
            <ServerLinks />
          </section>
          
          <InstallationDocSection onDownload={handleDownload} />
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
